import {
  newsletterPublicDe,
  newsletterPublicEn,
  newsletterPublicEs,
  newsletterPublicFr,
  newsletterPublicRu,
  newsletterPublicTr,
} from './newsletter-public-i18n';
import {
  newsletterEditorDe,
  newsletterEditorEn,
  newsletterEditorEs,
  newsletterEditorFr,
  newsletterEditorRu,
  newsletterEditorTr,
} from './newsletter-editor-i18n';
import {
  newsletterDeliverabilityDe,
  newsletterDeliverabilityEn,
  newsletterDeliverabilityEs,
  newsletterDeliverabilityFr,
  newsletterDeliverabilityRu,
  newsletterDeliverabilityTr,
} from './newsletter-deliverability-i18n';
import {
  newsletterAnalyticsDe,
  newsletterAnalyticsEn,
  newsletterAnalyticsEs,
  newsletterAnalyticsFr,
  newsletterAnalyticsRu,
  newsletterAnalyticsTr,
} from './newsletter-analytics-i18n';
import {
  newsletterAdministrationDe,
  newsletterAdministrationEn,
  newsletterAdministrationEs,
  newsletterAdministrationFr,
  newsletterAdministrationRu,
  newsletterAdministrationTr,
} from './newsletter-administration-i18n';

const statusDe = {
  PENDING: 'Ausstehend',
  ACTIVE: 'Aktiv',
  WITHDRAWN: 'Widerrufen',
  OBJECTED: 'Widersprochen',
  UNSUBSCRIBED: 'Abgemeldet',
  HARD_BOUNCED: 'Unzustellbar',
  SOFT_BOUNCED: 'Vorübergehend unzustellbar',
  COMPLAINED: 'Spam-Beschwerde',
  INVALID: 'Ungültig',
  BLOCKED: 'Gesperrt',
  EXPIRED: 'Abgelaufen',
} as const;
const statusEn = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  WITHDRAWN: 'Withdrawn',
  OBJECTED: 'Objected',
  UNSUBSCRIBED: 'Unsubscribed',
  HARD_BOUNCED: 'Hard bounced',
  SOFT_BOUNCED: 'Soft bounced',
  COMPLAINED: 'Spam complaint',
  INVALID: 'Invalid',
  BLOCKED: 'Blocked',
  EXPIRED: 'Expired',
} as const;
const statusTr = {
  PENDING: 'Beklemede',
  ACTIVE: 'Aktif',
  WITHDRAWN: 'Geri çekildi',
  OBJECTED: 'İtiraz edildi',
  UNSUBSCRIBED: 'Abonelikten çıkıldı',
  HARD_BOUNCED: 'Kalıcı teslim edilemedi',
  SOFT_BOUNCED: 'Geçici teslim edilemedi',
  COMPLAINED: 'Spam şikâyeti',
  INVALID: 'Geçersiz',
  BLOCKED: 'Engelli',
  EXPIRED: 'Süresi doldu',
} as const;
const statusFr = {
  PENDING: 'En attente',
  ACTIVE: 'Actif',
  WITHDRAWN: 'Retiré',
  OBJECTED: 'Opposition',
  UNSUBSCRIBED: 'Désabonné',
  HARD_BOUNCED: 'Rejet définitif',
  SOFT_BOUNCED: 'Rejet temporaire',
  COMPLAINED: 'Plainte pour spam',
  INVALID: 'Invalide',
  BLOCKED: 'Bloqué',
  EXPIRED: 'Expiré',
} as const;
const statusEs = {
  PENDING: 'Pendiente',
  ACTIVE: 'Activo',
  WITHDRAWN: 'Retirado',
  OBJECTED: 'Oposición',
  UNSUBSCRIBED: 'Baja',
  HARD_BOUNCED: 'Rebote permanente',
  SOFT_BOUNCED: 'Rebote temporal',
  COMPLAINED: 'Queja por spam',
  INVALID: 'No válido',
  BLOCKED: 'Bloqueado',
  EXPIRED: 'Caducado',
} as const;
const statusRu = {
  PENDING: 'Ожидает',
  ACTIVE: 'Активен',
  WITHDRAWN: 'Согласие отозвано',
  OBJECTED: 'Возражение',
  UNSUBSCRIBED: 'Отписан',
  HARD_BOUNCED: 'Постоянная ошибка',
  SOFT_BOUNCED: 'Временная ошибка',
  COMPLAINED: 'Жалоба на спам',
  INVALID: 'Недействителен',
  BLOCKED: 'Заблокирован',
  EXPIRED: 'Истёк',
} as const;

const legalBasis = {
  de: {
    EXPLICIT_CONSENT: 'Ausdrückliche Einwilligung',
    EXISTING_CUSTOMER_EXCEPTION: 'Bestandskunden-Ausnahme',
    MANUAL_DOCUMENTED_BASIS: 'Manuell dokumentierte Grundlage',
  },
  en: {
    EXPLICIT_CONSENT: 'Explicit consent',
    EXISTING_CUSTOMER_EXCEPTION: 'Existing-customer exception',
    MANUAL_DOCUMENTED_BASIS: 'Manually documented basis',
  },
  tr: {
    EXPLICIT_CONSENT: 'Açık rıza',
    EXISTING_CUSTOMER_EXCEPTION: 'Mevcut müşteri istisnası',
    MANUAL_DOCUMENTED_BASIS: 'Elle belgelenmiş dayanak',
  },
  fr: {
    EXPLICIT_CONSENT: 'Consentement explicite',
    EXISTING_CUSTOMER_EXCEPTION: 'Exception client existant',
    MANUAL_DOCUMENTED_BASIS: 'Base documentée manuellement',
  },
  es: {
    EXPLICIT_CONSENT: 'Consentimiento explícito',
    EXISTING_CUSTOMER_EXCEPTION: 'Excepción de cliente existente',
    MANUAL_DOCUMENTED_BASIS: 'Base documentada manualmente',
  },
  ru: {
    EXPLICIT_CONSENT: 'Явное согласие',
    EXISTING_CUSTOMER_EXCEPTION: 'Исключение для существующих клиентов',
    MANUAL_DOCUMENTED_BASIS: 'Основание, подтверждённое вручную',
  },
} as const;

const source = {
  de: {
    WEBSITE: 'Website',
    CUSTOMER_PORTAL: 'Kundenportal',
    INTERNAL_CUSTOMER: 'Kundenakte',
    INTERNAL_CONTACT: 'Kontaktakte',
    EMBEDDED_FORM: 'Eingebettetes Formular',
    IMPORT: 'Import',
    MANUAL_EVIDENCE: 'Manueller Nachweis',
    EXISTING_CUSTOMER: 'Bestandskunde',
    PREFERENCE_CENTER: 'Präferenz-Center',
    PROVIDER_WEBHOOK: 'Provider-Rückmeldung',
    SYSTEM: 'System',
  },
  en: {
    WEBSITE: 'Website',
    CUSTOMER_PORTAL: 'Customer portal',
    INTERNAL_CUSTOMER: 'Customer record',
    INTERNAL_CONTACT: 'Contact record',
    EMBEDDED_FORM: 'Embedded form',
    IMPORT: 'Import',
    MANUAL_EVIDENCE: 'Manual evidence',
    EXISTING_CUSTOMER: 'Existing customer',
    PREFERENCE_CENTER: 'Preference center',
    PROVIDER_WEBHOOK: 'Provider webhook',
    SYSTEM: 'System',
  },
  tr: {
    WEBSITE: 'Web sitesi',
    CUSTOMER_PORTAL: 'Müşteri portalı',
    INTERNAL_CUSTOMER: 'Müşteri kaydı',
    INTERNAL_CONTACT: 'İletişim kaydı',
    EMBEDDED_FORM: 'Gömülü form',
    IMPORT: 'İçe aktarma',
    MANUAL_EVIDENCE: 'Elle kanıt',
    EXISTING_CUSTOMER: 'Mevcut müşteri',
    PREFERENCE_CENTER: 'Tercih merkezi',
    PROVIDER_WEBHOOK: 'Sağlayıcı bildirimi',
    SYSTEM: 'Sistem',
  },
  fr: {
    WEBSITE: 'Site web',
    CUSTOMER_PORTAL: 'Portail client',
    INTERNAL_CUSTOMER: 'Fiche client',
    INTERNAL_CONTACT: 'Fiche contact',
    EMBEDDED_FORM: 'Formulaire intégré',
    IMPORT: 'Import',
    MANUAL_EVIDENCE: 'Preuve manuelle',
    EXISTING_CUSTOMER: 'Client existant',
    PREFERENCE_CENTER: 'Centre de préférences',
    PROVIDER_WEBHOOK: 'Retour du fournisseur',
    SYSTEM: 'Système',
  },
  es: {
    WEBSITE: 'Sitio web',
    CUSTOMER_PORTAL: 'Portal de clientes',
    INTERNAL_CUSTOMER: 'Ficha de cliente',
    INTERNAL_CONTACT: 'Ficha de contacto',
    EMBEDDED_FORM: 'Formulario integrado',
    IMPORT: 'Importación',
    MANUAL_EVIDENCE: 'Prueba manual',
    EXISTING_CUSTOMER: 'Cliente existente',
    PREFERENCE_CENTER: 'Centro de preferencias',
    PROVIDER_WEBHOOK: 'Notificación del proveedor',
    SYSTEM: 'Sistema',
  },
  ru: {
    WEBSITE: 'Веб-сайт',
    CUSTOMER_PORTAL: 'Клиентский портал',
    INTERNAL_CUSTOMER: 'Карточка клиента',
    INTERNAL_CONTACT: 'Карточка контакта',
    EMBEDDED_FORM: 'Встроенная форма',
    IMPORT: 'Импорт',
    MANUAL_EVIDENCE: 'Ручное подтверждение',
    EXISTING_CUSTOMER: 'Существующий клиент',
    PREFERENCE_CENTER: 'Центр предпочтений',
    PROVIDER_WEBHOOK: 'Сообщение провайдера',
    SYSTEM: 'Система',
  },
} as const;

const event = {
  de: {
    REQUESTED: 'Angefordert',
    CONFIRMED: 'Bestätigt',
    MANUAL_DOCUMENTED: 'Manuell dokumentiert',
    TOPIC_SUBSCRIBED: 'Thema abonniert',
    TOPIC_UNSUBSCRIBED: 'Thema abgemeldet',
    LANGUAGE_CHANGED: 'Sprache geändert',
    TRACKING_GRANTED: 'Tracking-Einwilligung erteilt',
    TRACKING_WITHDRAWN: 'Tracking-Einwilligung widerrufen',
    WITHDRAWN: 'Einwilligung widerrufen',
    OBJECTED: 'Widerspruch erklärt',
    UNSUBSCRIBED: 'Abgemeldet',
    BLOCKED: 'Gesperrt',
    EXPIRED: 'Abgelaufen',
    CORRECTED: 'Nachweis ergänzt',
    EXISTING_CUSTOMER_SUBMITTED: 'Bestandskundenprüfung eingereicht',
    EXISTING_CUSTOMER_APPROVED: 'Bestandskundenprüfung freigegeben',
    EXISTING_CUSTOMER_REJECTED: 'Bestandskundenprüfung abgelehnt',
    EXISTING_CUSTOMER_REVOKED: 'Bestandskundenfreigabe widerrufen',
    SUPPRESSION_APPLIED: 'Sperre angewendet',
    SUPPRESSION_LIFTED: 'Manuelle Sperre aufgehoben',
  },
  en: {
    REQUESTED: 'Requested',
    CONFIRMED: 'Confirmed',
    MANUAL_DOCUMENTED: 'Manually documented',
    TOPIC_SUBSCRIBED: 'Topic subscribed',
    TOPIC_UNSUBSCRIBED: 'Topic unsubscribed',
    LANGUAGE_CHANGED: 'Language changed',
    TRACKING_GRANTED: 'Tracking consent granted',
    TRACKING_WITHDRAWN: 'Tracking consent withdrawn',
    WITHDRAWN: 'Consent withdrawn',
    OBJECTED: 'Objection recorded',
    UNSUBSCRIBED: 'Unsubscribed',
    BLOCKED: 'Blocked',
    EXPIRED: 'Expired',
    CORRECTED: 'Evidence corrected',
    EXISTING_CUSTOMER_SUBMITTED: 'Existing-customer review submitted',
    EXISTING_CUSTOMER_APPROVED: 'Existing-customer review approved',
    EXISTING_CUSTOMER_REJECTED: 'Existing-customer review rejected',
    EXISTING_CUSTOMER_REVOKED: 'Existing-customer approval revoked',
    SUPPRESSION_APPLIED: 'Suppression applied',
    SUPPRESSION_LIFTED: 'Manual suppression lifted',
  },
  tr: {
    REQUESTED: 'Talep edildi',
    CONFIRMED: 'Onaylandı',
    MANUAL_DOCUMENTED: 'Elle belgelendi',
    TOPIC_SUBSCRIBED: 'Konuya abone olundu',
    TOPIC_UNSUBSCRIBED: 'Konu aboneliği kaldırıldı',
    LANGUAGE_CHANGED: 'Dil değiştirildi',
    TRACKING_GRANTED: 'Takip izni verildi',
    TRACKING_WITHDRAWN: 'Takip izni geri çekildi',
    WITHDRAWN: 'Rıza geri çekildi',
    OBJECTED: 'İtiraz kaydedildi',
    UNSUBSCRIBED: 'Abonelikten çıkıldı',
    BLOCKED: 'Engellendi',
    EXPIRED: 'Süresi doldu',
    CORRECTED: 'Kanıt düzeltildi',
    EXISTING_CUSTOMER_SUBMITTED: 'Mevcut müşteri incelemesi gönderildi',
    EXISTING_CUSTOMER_APPROVED: 'Mevcut müşteri incelemesi onaylandı',
    EXISTING_CUSTOMER_REJECTED: 'Mevcut müşteri incelemesi reddedildi',
    EXISTING_CUSTOMER_REVOKED: 'Mevcut müşteri onayı geri alındı',
    SUPPRESSION_APPLIED: 'Engelleme uygulandı',
    SUPPRESSION_LIFTED: 'Elle engelleme kaldırıldı',
  },
  fr: {
    REQUESTED: 'Demandé',
    CONFIRMED: 'Confirmé',
    MANUAL_DOCUMENTED: 'Documenté manuellement',
    TOPIC_SUBSCRIBED: 'Thème abonné',
    TOPIC_UNSUBSCRIBED: 'Thème désabonné',
    LANGUAGE_CHANGED: 'Langue modifiée',
    TRACKING_GRANTED: 'Consentement au suivi accordé',
    TRACKING_WITHDRAWN: 'Consentement au suivi retiré',
    WITHDRAWN: 'Consentement retiré',
    OBJECTED: 'Opposition enregistrée',
    UNSUBSCRIBED: 'Désabonné',
    BLOCKED: 'Bloqué',
    EXPIRED: 'Expiré',
    CORRECTED: 'Preuve corrigée',
    EXISTING_CUSTOMER_SUBMITTED: 'Examen client existant soumis',
    EXISTING_CUSTOMER_APPROVED: 'Examen client existant approuvé',
    EXISTING_CUSTOMER_REJECTED: 'Examen client existant refusé',
    EXISTING_CUSTOMER_REVOKED: 'Autorisation client existant révoquée',
    SUPPRESSION_APPLIED: 'Exclusion appliquée',
    SUPPRESSION_LIFTED: 'Exclusion manuelle levée',
  },
  es: {
    REQUESTED: 'Solicitado',
    CONFIRMED: 'Confirmado',
    MANUAL_DOCUMENTED: 'Documentado manualmente',
    TOPIC_SUBSCRIBED: 'Tema suscrito',
    TOPIC_UNSUBSCRIBED: 'Tema cancelado',
    LANGUAGE_CHANGED: 'Idioma cambiado',
    TRACKING_GRANTED: 'Consentimiento de seguimiento otorgado',
    TRACKING_WITHDRAWN: 'Consentimiento de seguimiento retirado',
    WITHDRAWN: 'Consentimiento retirado',
    OBJECTED: 'Oposición registrada',
    UNSUBSCRIBED: 'Baja',
    BLOCKED: 'Bloqueado',
    EXPIRED: 'Caducado',
    CORRECTED: 'Prueba corregida',
    EXISTING_CUSTOMER_SUBMITTED: 'Revisión de cliente existente enviada',
    EXISTING_CUSTOMER_APPROVED: 'Revisión de cliente existente aprobada',
    EXISTING_CUSTOMER_REJECTED: 'Revisión de cliente existente rechazada',
    EXISTING_CUSTOMER_REVOKED: 'Aprobación de cliente existente revocada',
    SUPPRESSION_APPLIED: 'Exclusión aplicada',
    SUPPRESSION_LIFTED: 'Exclusión manual levantada',
  },
  ru: {
    REQUESTED: 'Запрошено',
    CONFIRMED: 'Подтверждено',
    MANUAL_DOCUMENTED: 'Подтверждено вручную',
    TOPIC_SUBSCRIBED: 'Тема подключена',
    TOPIC_UNSUBSCRIBED: 'Тема отключена',
    LANGUAGE_CHANGED: 'Язык изменён',
    TRACKING_GRANTED: 'Согласие на отслеживание дано',
    TRACKING_WITHDRAWN: 'Согласие на отслеживание отозвано',
    WITHDRAWN: 'Согласие отозвано',
    OBJECTED: 'Возражение зарегистрировано',
    UNSUBSCRIBED: 'Отписан',
    BLOCKED: 'Заблокирован',
    EXPIRED: 'Истёк',
    CORRECTED: 'Подтверждение исправлено',
    EXISTING_CUSTOMER_SUBMITTED: 'Проверка постоянного клиента отправлена',
    EXISTING_CUSTOMER_APPROVED: 'Проверка постоянного клиента одобрена',
    EXISTING_CUSTOMER_REJECTED: 'Проверка постоянного клиента отклонена',
    EXISTING_CUSTOMER_REVOKED: 'Разрешение постоянного клиента отозвано',
    SUPPRESSION_APPLIED: 'Блокировка применена',
    SUPPRESSION_LIFTED: 'Ручная блокировка снята',
  },
} as const;

const complianceDe = {
  warningTitle: 'Technische Vorprüfung – keine automatische Rechtmäßigkeitsaussage',
  existing: {
    tab: 'Bestandskundenprüfung',
    submitTitle: 'Bestandskunden-Ausnahme dokumentieren',
    submitDescription:
      'Der Nachweis wird zunächst gesperrt eingereicht und muss von einer zweiten berechtigten Person geprüft werden.',
    warning:
      'Diese Ausnahme ist eng auszulegen, standardmäßig deaktiviert und vor dem Produktivbetrieb rechtlich zu prüfen. Fehlt eine Voraussetzung, bleibt der Empfänger gesperrt.',
    acknowledgement:
      'Ich bestätige, dass keine gekaufte Liste verwendet wird, alle Angaben anhand des Verkaufsnachweises geprüft wurden und die Einreichung noch keine Versandfreigabe ist.',
    submit: 'Zur Prüfung einreichen',
    settingsTitle: 'Mandanteneinstellung',
    enable: 'Bestandskunden-Ausnahme für diesen Mandanten aktivieren',
    reviewDays: 'Erneute Prüfung nach Tagen',
    settingsAcknowledgement:
      'Ich habe verstanden, dass diese technische Einstellung keine rechtliche Freigabe ersetzt.',
    listTitle: 'Dokumentierte Prüfungen',
    empty: 'Noch keine Bestandskundenprüfung vorhanden.',
  },
  fields: {
    customer: 'Kunde',
    selectCustomer: 'Kunden mit E-Mail auswählen',
    selectTopic: 'Newsletter-Thema auswählen',
    acquiredAt: 'Zeitpunkt der Adresserhebung',
    acquisitionSource: 'Quelle und Erhebungskontext',
    emailIdentity: 'Dokumentierte E-Mail-Identität',
    salesDocument: 'Verkaufsbeleg',
    selectDocument: 'Qualifizierenden Beleg auswählen',
    noticeEvidence: 'Nachweis des Hinweises bei Erhebung',
    objectionVersion: 'Version des Widerspruchshinweises',
    objectionText: 'Vollständiger Widerspruchshinweis',
    productArea: 'Produkt- oder Leistungsbereich',
    similarity: 'Ähnlichkeitskategorie',
    validationReason: 'Begründung der Einzelfallprüfung',
    validUntil: 'Erneute Prüfung bis',
  },
  conditions: {
    emailObtainedInSale: 'Die E-Mail-Adresse wurde im Zusammenhang mit diesem Verkauf erhoben.',
    ownSimilarProducts:
      'Die geplante Werbung betrifft ausschließlich eigene ähnliche Waren oder Dienstleistungen.',
    noObjection: 'Es liegt kein Widerspruch und keine andere wirksame Sperre vor.',
    objectionNoticeAtCollection:
      'Bei Erhebung wurde nachweisbar auf die Widerspruchsmöglichkeit hingewiesen.',
    objectionNoticeEveryUse:
      'Bei jeder Verwendung wird erneut klar und kostenlos auf die Widerspruchsmöglichkeit hingewiesen.',
  },
  review: {
    approveTitle: 'Prüfung freigeben',
    rejectTitle: 'Prüfung ablehnen',
    revokeTitle: 'Freigabe widerrufen',
    description: 'Die Entscheidung und ihre Begründung werden unveränderlich protokolliert.',
  },
  actions: { approve: 'Freigeben', reject: 'Ablehnen', revoke: 'Widerrufen' },
  basisStatus: {
    PENDING: 'In Prüfung',
    APPROVED: 'Freigegeben',
    REJECTED: 'Abgelehnt',
    REVOKED: 'Widerrufen',
    EXPIRED: 'Prüfung abgelaufen',
  },
  suppression: {
    tab: 'Sperrliste',
    title: 'Suppression hat Vorrang',
    description:
      'Wirksame Sperren werden bei Zielgruppenbildung und unmittelbar vor jedem Einzelversand geprüft. Transaktions-E-Mails bleiben getrennt.',
    createTitle: 'Sperre dokumentieren',
    scope: 'Sperrumfang',
    reason: 'Sperrgrund',
    apply: 'Sperre anwenden',
    activeTitle: 'Wirksame Sperren',
    appliedAt: 'Angewendet am',
    lift: 'Manuelle Sperre aufheben',
    noReactivation:
      'Das Aufheben einer manuellen Sperre aktiviert keine Einwilligung und kein Abonnement automatisch.',
  },
  scope: { TENANT: 'Mandantenweit', BRAND: 'Absendermarke', TOPIC: 'Newsletter-Thema' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Globale Abmeldung',
    TOPIC_UNSUBSCRIBE: 'Themenabmeldung',
    OBJECTION: 'Widerspruch',
    SPAM_COMPLAINT: 'Spam-Beschwerde',
    HARD_BOUNCE: 'Hard Bounce',
    INVALID_ADDRESS: 'Ungültige Adresse',
    MANUAL_BLOCK: 'Manuelle Sperre',
    DELETED_IDENTITY: 'Gelöschte Identität',
  },
  validation: {
    completeEvidence:
      'Bitte Verkaufsbezug, E-Mail-Identität, alle fünf Voraussetzungen, vollständige Texte und Nachweise erfassen.',
  },
  messages: {
    submitted: 'Die Prüfung wurde gesperrt eingereicht.',
    reviewed: 'Die Entscheidung wurde protokolliert.',
    settingsSaved: 'Die Mandanteneinstellung wurde gespeichert.',
    suppressed: 'Die Sperre wurde sofort wirksam.',
    lifted: 'Die manuelle Sperre wurde ohne automatische Reaktivierung aufgehoben.',
  },
} as const;

const complianceEn = {
  warningTitle: 'Technical pre-check — no automatic legality statement',
  existing: {
    tab: 'Existing-customer review',
    submitTitle: 'Document existing-customer exception',
    submitDescription:
      'Evidence is submitted in a blocked state and must be reviewed by another authorised person.',
    warning:
      'This exception is narrow, disabled by default and requires legal review before production. A missing condition keeps the recipient blocked.',
    acknowledgement:
      'I confirm that no purchased list is used, every statement was checked against the sale evidence, and submission is not yet permission to send.',
    submit: 'Submit for review',
    settingsTitle: 'Tenant setting',
    enable: 'Enable the existing-customer exception for this tenant',
    reviewDays: 'Re-review after days',
    settingsAcknowledgement:
      'I understand that this technical setting does not replace legal approval.',
    listTitle: 'Documented reviews',
    empty: 'No existing-customer review yet.',
  },
  fields: {
    customer: 'Customer',
    selectCustomer: 'Select customer with email',
    selectTopic: 'Select newsletter topic',
    acquiredAt: 'Address collection time',
    acquisitionSource: 'Source and collection context',
    emailIdentity: 'Documented email identity',
    salesDocument: 'Sales document',
    selectDocument: 'Select qualifying document',
    noticeEvidence: 'Evidence of notice at collection',
    objectionVersion: 'Objection-notice version',
    objectionText: 'Full objection notice',
    productArea: 'Product or service area',
    similarity: 'Similarity category',
    validationReason: 'Individual assessment rationale',
    validUntil: 'Re-review by',
  },
  conditions: {
    emailObtainedInSale: 'The email address was obtained in connection with this sale.',
    ownSimilarProducts:
      'The planned advertising concerns only the company’s own similar goods or services.',
    noObjection: 'No objection or other effective suppression exists.',
    objectionNoticeAtCollection:
      'The right to object was demonstrably stated when the address was collected.',
    objectionNoticeEveryUse: 'Every use will again clearly state the free right to object.',
  },
  review: {
    approveTitle: 'Approve review',
    rejectTitle: 'Reject review',
    revokeTitle: 'Revoke approval',
    description: 'The decision and rationale are recorded immutably.',
  },
  actions: { approve: 'Approve', reject: 'Reject', revoke: 'Revoke' },
  basisStatus: {
    PENDING: 'In review',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    REVOKED: 'Revoked',
    EXPIRED: 'Review expired',
  },
  suppression: {
    tab: 'Suppression',
    title: 'Suppression takes priority',
    description:
      'Effective suppressions are checked during audience creation and immediately before every individual send. Transactional email remains separate.',
    createTitle: 'Document suppression',
    scope: 'Scope',
    reason: 'Reason',
    apply: 'Apply suppression',
    activeTitle: 'Effective suppressions',
    appliedAt: 'Applied at',
    lift: 'Lift manual suppression',
    noReactivation:
      'Lifting a manual suppression does not automatically activate consent or a subscription.',
  },
  scope: { TENANT: 'Tenant-wide', BRAND: 'Sender brand', TOPIC: 'Newsletter topic' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Global unsubscribe',
    TOPIC_UNSUBSCRIBE: 'Topic unsubscribe',
    OBJECTION: 'Objection',
    SPAM_COMPLAINT: 'Spam complaint',
    HARD_BOUNCE: 'Hard bounce',
    INVALID_ADDRESS: 'Invalid address',
    MANUAL_BLOCK: 'Manual block',
    DELETED_IDENTITY: 'Deleted identity',
  },
  validation: {
    completeEvidence:
      'Provide the sale reference, email identity, all five conditions, complete texts and evidence.',
  },
  messages: {
    submitted: 'The review was submitted in blocked state.',
    reviewed: 'The decision was recorded.',
    settingsSaved: 'Tenant setting saved.',
    suppressed: 'The suppression took effect immediately.',
    lifted: 'The manual suppression was lifted without automatic reactivation.',
  },
} as const;

const complianceTr = {
  warningTitle: 'Teknik ön kontrol — otomatik hukuka uygunluk beyanı değildir',
  existing: {
    tab: 'Mevcut müşteri incelemesi',
    submitTitle: 'Mevcut müşteri istisnasını belgele',
    submitDescription:
      'Kanıt engelli durumda gönderilir ve başka bir yetkili kişi tarafından incelenmelidir.',
    warning:
      'Bu istisna dar yorumlanır, varsayılan olarak kapalıdır ve üretimden önce hukuk incelemesi gerektirir. Eksik koşul alıcıyı engelli tutar.',
    acknowledgement:
      'Satın alınmış liste kullanılmadığını, tüm bilgilerin satış kanıtıyla kontrol edildiğini ve gönderimin henüz gönderme izni olmadığını onaylıyorum.',
    submit: 'İncelemeye gönder',
    settingsTitle: 'Kiracı ayarı',
    enable: 'Bu kiracı için mevcut müşteri istisnasını etkinleştir',
    reviewDays: 'Şu kadar gün sonra yeniden incele',
    settingsAcknowledgement: 'Bu teknik ayarın hukuk onayının yerini almadığını anlıyorum.',
    listTitle: 'Belgelenmiş incelemeler',
    empty: 'Henüz mevcut müşteri incelemesi yok.',
  },
  fields: {
    customer: 'Müşteri',
    selectCustomer: 'E-postalı müşteri seç',
    selectTopic: 'Bülten konusu seç',
    acquiredAt: 'Adresin alındığı zaman',
    acquisitionSource: 'Kaynak ve edinim bağlamı',
    emailIdentity: 'Belgelenmiş e-posta kimliği',
    salesDocument: 'Satış belgesi',
    selectDocument: 'Uygun belgeyi seç',
    noticeEvidence: 'Edinimdeki bilgilendirme kanıtı',
    objectionVersion: 'İtiraz bildirimi sürümü',
    objectionText: 'Tam itiraz bildirimi',
    productArea: 'Ürün veya hizmet alanı',
    similarity: 'Benzerlik kategorisi',
    validationReason: 'Bireysel değerlendirme gerekçesi',
    validUntil: 'Yeniden inceleme tarihi',
  },
  conditions: {
    emailObtainedInSale: 'E-posta adresi bu satışla bağlantılı olarak alındı.',
    ownSimilarProducts:
      'Planlanan reklam yalnızca işletmenin kendi benzer ürün veya hizmetleriyle ilgilidir.',
    noObjection: 'İtiraz veya başka etkin engel yoktur.',
    objectionNoticeAtCollection: 'Adres alınırken itiraz hakkı kanıtlanabilir biçimde bildirildi.',
    objectionNoticeEveryUse: 'Her kullanımda ücretsiz itiraz hakkı yeniden açıkça bildirilecektir.',
  },
  review: {
    approveTitle: 'İncelemeyi onayla',
    rejectTitle: 'İncelemeyi reddet',
    revokeTitle: 'Onayı geri al',
    description: 'Karar ve gerekçesi değiştirilemez biçimde kaydedilir.',
  },
  actions: { approve: 'Onayla', reject: 'Reddet', revoke: 'Geri al' },
  basisStatus: {
    PENDING: 'İncelemede',
    APPROVED: 'Onaylandı',
    REJECTED: 'Reddedildi',
    REVOKED: 'Geri alındı',
    EXPIRED: 'İnceleme süresi doldu',
  },
  suppression: {
    tab: 'Engelleme',
    title: 'Engelleme önceliklidir',
    description:
      'Etkin engeller hedef kitle oluşturulurken ve her tekil gönderimden hemen önce kontrol edilir. İşlemsel e-posta ayrıdır.',
    createTitle: 'Engeli belgele',
    scope: 'Kapsam',
    reason: 'Neden',
    apply: 'Engeli uygula',
    activeTitle: 'Etkin engeller',
    appliedAt: 'Uygulama zamanı',
    lift: 'Elle engeli kaldır',
    noReactivation: 'Elle engeli kaldırmak rızayı veya aboneliği otomatik etkinleştirmez.',
  },
  scope: { TENANT: 'Kiracı geneli', BRAND: 'Gönderen marka', TOPIC: 'Bülten konusu' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Genel abonelikten çıkma',
    TOPIC_UNSUBSCRIBE: 'Konu aboneliğinden çıkma',
    OBJECTION: 'İtiraz',
    SPAM_COMPLAINT: 'Spam şikâyeti',
    HARD_BOUNCE: 'Kalıcı geri dönme',
    INVALID_ADDRESS: 'Geçersiz adres',
    MANUAL_BLOCK: 'Elle engel',
    DELETED_IDENTITY: 'Silinmiş kimlik',
  },
  validation: {
    completeEvidence:
      'Satış bağlantısını, e-posta kimliğini, beş koşulun tümünü, tam metinleri ve kanıtı girin.',
  },
  messages: {
    submitted: 'İnceleme engelli durumda gönderildi.',
    reviewed: 'Karar kaydedildi.',
    settingsSaved: 'Kiracı ayarı kaydedildi.',
    suppressed: 'Engel hemen yürürlüğe girdi.',
    lifted: 'Elle engel otomatik etkinleştirme olmadan kaldırıldı.',
  },
} as const;

const complianceFr = {
  warningTitle: 'Précontrôle technique — aucune déclaration automatique de licéité',
  existing: {
    tab: 'Examen client existant',
    submitTitle: 'Documenter l’exception client existant',
    submitDescription:
      'La preuve est soumise en état bloqué et doit être examinée par une autre personne autorisée.',
    warning:
      'Cette exception est stricte, désactivée par défaut et exige un examen juridique avant la production. Une condition manquante maintient le destinataire bloqué.',
    acknowledgement:
      'Je confirme qu’aucune liste achetée n’est utilisée, que chaque indication a été vérifiée avec la preuve de vente et que la soumission ne vaut pas encore autorisation d’envoi.',
    submit: 'Soumettre à examen',
    settingsTitle: 'Réglage du tenant',
    enable: 'Activer l’exception client existant pour ce tenant',
    reviewDays: 'Réexaminer après ce nombre de jours',
    settingsAcknowledgement:
      'Je comprends que ce réglage technique ne remplace pas une validation juridique.',
    listTitle: 'Examens documentés',
    empty: 'Aucun examen client existant.',
  },
  fields: {
    customer: 'Client',
    selectCustomer: 'Choisir un client avec e-mail',
    selectTopic: 'Choisir un thème',
    acquiredAt: 'Date de collecte de l’adresse',
    acquisitionSource: 'Source et contexte de collecte',
    emailIdentity: 'Identité e-mail documentée',
    salesDocument: 'Document de vente',
    selectDocument: 'Choisir un document admissible',
    noticeEvidence: 'Preuve de l’information à la collecte',
    objectionVersion: 'Version de l’avis d’opposition',
    objectionText: 'Avis d’opposition complet',
    productArea: 'Domaine de produit ou service',
    similarity: 'Catégorie de similarité',
    validationReason: 'Motif de l’évaluation individuelle',
    validUntil: 'Réexamen avant le',
  },
  conditions: {
    emailObtainedInSale: 'L’adresse e-mail a été obtenue dans le cadre de cette vente.',
    ownSimilarProducts:
      'La publicité prévue porte uniquement sur des biens ou services propres et similaires.',
    noObjection: 'Aucune opposition ni autre exclusion effective n’existe.',
    objectionNoticeAtCollection:
      'Le droit d’opposition a été indiqué de façon démontrable lors de la collecte.',
    objectionNoticeEveryUse:
      'Chaque utilisation rappellera clairement le droit d’opposition gratuit.',
  },
  review: {
    approveTitle: 'Approuver l’examen',
    rejectTitle: 'Refuser l’examen',
    revokeTitle: 'Révoquer l’approbation',
    description: 'La décision et son motif sont consignés de façon immuable.',
  },
  actions: { approve: 'Approuver', reject: 'Refuser', revoke: 'Révoquer' },
  basisStatus: {
    PENDING: 'En examen',
    APPROVED: 'Approuvé',
    REJECTED: 'Refusé',
    REVOKED: 'Révoqué',
    EXPIRED: 'Examen expiré',
  },
  suppression: {
    tab: 'Exclusion',
    title: 'L’exclusion est prioritaire',
    description:
      'Les exclusions effectives sont contrôlées lors de la création de l’audience et juste avant chaque envoi individuel. Les e-mails transactionnels restent séparés.',
    createTitle: 'Documenter une exclusion',
    scope: 'Portée',
    reason: 'Motif',
    apply: 'Appliquer',
    activeTitle: 'Exclusions effectives',
    appliedAt: 'Appliquée le',
    lift: 'Lever le blocage manuel',
    noReactivation:
      'Lever un blocage manuel ne réactive automatiquement ni consentement ni abonnement.',
  },
  scope: { TENANT: 'Tout le tenant', BRAND: 'Marque expéditrice', TOPIC: 'Thème de newsletter' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Désabonnement global',
    TOPIC_UNSUBSCRIBE: 'Désabonnement du thème',
    OBJECTION: 'Opposition',
    SPAM_COMPLAINT: 'Plainte pour spam',
    HARD_BOUNCE: 'Rejet définitif',
    INVALID_ADDRESS: 'Adresse invalide',
    MANUAL_BLOCK: 'Blocage manuel',
    DELETED_IDENTITY: 'Identité supprimée',
  },
  validation: {
    completeEvidence:
      'Renseignez la vente, l’identité e-mail, les cinq conditions, les textes complets et les preuves.',
  },
  messages: {
    submitted: 'L’examen a été soumis en état bloqué.',
    reviewed: 'La décision a été consignée.',
    settingsSaved: 'Réglage du tenant enregistré.',
    suppressed: 'L’exclusion a pris effet immédiatement.',
    lifted: 'Le blocage manuel a été levé sans réactivation automatique.',
  },
} as const;

const complianceEs = {
  warningTitle: 'Comprobación técnica previa — sin declaración automática de licitud',
  existing: {
    tab: 'Revisión de cliente existente',
    submitTitle: 'Documentar la excepción de cliente existente',
    submitDescription: 'La prueba se presenta bloqueada y debe revisarla otra persona autorizada.',
    warning:
      'Esta excepción es estricta, está desactivada por defecto y requiere revisión jurídica antes de producción. Si falta una condición, el destinatario permanece bloqueado.',
    acknowledgement:
      'Confirmo que no se usa una lista comprada, que cada dato se contrastó con la prueba de venta y que el envío aún no autoriza a enviar.',
    submit: 'Enviar a revisión',
    settingsTitle: 'Configuración del tenant',
    enable: 'Activar la excepción de cliente existente para este tenant',
    reviewDays: 'Revisar de nuevo tras días',
    settingsAcknowledgement:
      'Entiendo que esta configuración técnica no sustituye la aprobación jurídica.',
    listTitle: 'Revisiones documentadas',
    empty: 'Aún no hay revisiones de clientes existentes.',
  },
  fields: {
    customer: 'Cliente',
    selectCustomer: 'Seleccionar cliente con correo',
    selectTopic: 'Seleccionar tema',
    acquiredAt: 'Momento de obtención de la dirección',
    acquisitionSource: 'Fuente y contexto de obtención',
    emailIdentity: 'Identidad de correo documentada',
    salesDocument: 'Documento de venta',
    selectDocument: 'Seleccionar documento válido',
    noticeEvidence: 'Prueba del aviso al obtenerla',
    objectionVersion: 'Versión del aviso de oposición',
    objectionText: 'Aviso de oposición completo',
    productArea: 'Área de producto o servicio',
    similarity: 'Categoría de similitud',
    validationReason: 'Motivo de evaluación individual',
    validUntil: 'Revisar antes de',
  },
  conditions: {
    emailObtainedInSale: 'La dirección se obtuvo en relación con esta venta.',
    ownSimilarProducts: 'La publicidad prevista trata solo bienes o servicios propios similares.',
    noObjection: 'No existe oposición ni otra exclusión efectiva.',
    objectionNoticeAtCollection:
      'Al obtener la dirección se informó de forma demostrable del derecho de oposición.',
    objectionNoticeEveryUse:
      'Cada uso volverá a indicar claramente el derecho gratuito de oposición.',
  },
  review: {
    approveTitle: 'Aprobar revisión',
    rejectTitle: 'Rechazar revisión',
    revokeTitle: 'Revocar aprobación',
    description: 'La decisión y su motivo se registran de forma inmutable.',
  },
  actions: { approve: 'Aprobar', reject: 'Rechazar', revoke: 'Revocar' },
  basisStatus: {
    PENDING: 'En revisión',
    APPROVED: 'Aprobada',
    REJECTED: 'Rechazada',
    REVOKED: 'Revocada',
    EXPIRED: 'Revisión caducada',
  },
  suppression: {
    tab: 'Exclusión',
    title: 'La exclusión tiene prioridad',
    description:
      'Las exclusiones efectivas se comprueban al crear la audiencia y justo antes de cada envío individual. El correo transaccional permanece separado.',
    createTitle: 'Documentar exclusión',
    scope: 'Alcance',
    reason: 'Motivo',
    apply: 'Aplicar exclusión',
    activeTitle: 'Exclusiones efectivas',
    appliedAt: 'Aplicada el',
    lift: 'Levantar bloqueo manual',
    noReactivation:
      'Levantar un bloqueo manual no reactiva automáticamente el consentimiento ni la suscripción.',
  },
  scope: { TENANT: 'Todo el tenant', BRAND: 'Marca remitente', TOPIC: 'Tema del boletín' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Baja global',
    TOPIC_UNSUBSCRIBE: 'Baja del tema',
    OBJECTION: 'Oposición',
    SPAM_COMPLAINT: 'Queja de spam',
    HARD_BOUNCE: 'Rebote permanente',
    INVALID_ADDRESS: 'Dirección no válida',
    MANUAL_BLOCK: 'Bloqueo manual',
    DELETED_IDENTITY: 'Identidad eliminada',
  },
  validation: {
    completeEvidence:
      'Indique la venta, identidad de correo, las cinco condiciones, textos completos y pruebas.',
  },
  messages: {
    submitted: 'La revisión se envió bloqueada.',
    reviewed: 'La decisión quedó registrada.',
    settingsSaved: 'Configuración del tenant guardada.',
    suppressed: 'La exclusión entró en vigor de inmediato.',
    lifted: 'El bloqueo manual se levantó sin reactivación automática.',
  },
} as const;

const complianceRu = {
  warningTitle: 'Техническая предварительная проверка — не автоматическое заключение о законности',
  existing: {
    tab: 'Проверка постоянного клиента',
    submitTitle: 'Документировать исключение для постоянного клиента',
    submitDescription:
      'Доказательство подаётся в заблокированном состоянии и проверяется другим уполномоченным сотрудником.',
    warning:
      'Исключение толкуется узко, по умолчанию выключено и требует юридической проверки до эксплуатации. При отсутствии условия получатель остаётся заблокирован.',
    acknowledgement:
      'Подтверждаю, что купленный список не используется, все сведения сверены с продажей, а подача ещё не разрешает отправку.',
    submit: 'Отправить на проверку',
    settingsTitle: 'Настройка организации',
    enable: 'Включить исключение для постоянных клиентов этой организации',
    reviewDays: 'Повторная проверка через дней',
    settingsAcknowledgement:
      'Я понимаю, что техническая настройка не заменяет юридическое согласование.',
    listTitle: 'Документированные проверки',
    empty: 'Проверок постоянных клиентов пока нет.',
  },
  fields: {
    customer: 'Клиент',
    selectCustomer: 'Выбрать клиента с email',
    selectTopic: 'Выбрать тему',
    acquiredAt: 'Время получения адреса',
    acquisitionSource: 'Источник и контекст получения',
    emailIdentity: 'Документированная email-идентичность',
    salesDocument: 'Документ продажи',
    selectDocument: 'Выбрать подходящий документ',
    noticeEvidence: 'Доказательство уведомления при сборе',
    objectionVersion: 'Версия уведомления о возражении',
    objectionText: 'Полный текст уведомления о возражении',
    productArea: 'Область товара или услуги',
    similarity: 'Категория сходства',
    validationReason: 'Обоснование индивидуальной оценки',
    validUntil: 'Повторная проверка до',
  },
  conditions: {
    emailObtainedInSale: 'Адрес email получен в связи с этой продажей.',
    ownSimilarProducts:
      'Планируемая реклама касается только собственных сходных товаров или услуг.',
    noObjection: 'Возражений и иных действующих блокировок нет.',
    objectionNoticeAtCollection: 'При сборе адреса доказуемо сообщено право на возражение.',
    objectionNoticeEveryUse:
      'При каждом использовании вновь ясно сообщается бесплатное право на возражение.',
  },
  review: {
    approveTitle: 'Одобрить проверку',
    rejectTitle: 'Отклонить проверку',
    revokeTitle: 'Отозвать одобрение',
    description: 'Решение и обоснование записываются неизменяемо.',
  },
  actions: { approve: 'Одобрить', reject: 'Отклонить', revoke: 'Отозвать' },
  basisStatus: {
    PENDING: 'На проверке',
    APPROVED: 'Одобрено',
    REJECTED: 'Отклонено',
    REVOKED: 'Отозвано',
    EXPIRED: 'Проверка просрочена',
  },
  suppression: {
    tab: 'Блокировки',
    title: 'Блокировка имеет приоритет',
    description:
      'Действующие блокировки проверяются при формировании аудитории и непосредственно перед каждой отправкой. Транзакционные письма обрабатываются отдельно.',
    createTitle: 'Документировать блокировку',
    scope: 'Область',
    reason: 'Причина',
    apply: 'Применить блокировку',
    activeTitle: 'Действующие блокировки',
    appliedAt: 'Применено',
    lift: 'Снять ручную блокировку',
    noReactivation: 'Снятие ручной блокировки не активирует автоматически согласие или подписку.',
  },
  scope: { TENANT: 'Вся организация', BRAND: 'Бренд отправителя', TOPIC: 'Тема рассылки' },
  suppressionReason: {
    GLOBAL_UNSUBSCRIBE: 'Полная отписка',
    TOPIC_UNSUBSCRIBE: 'Отписка от темы',
    OBJECTION: 'Возражение',
    SPAM_COMPLAINT: 'Жалоба на спам',
    HARD_BOUNCE: 'Постоянный отказ',
    INVALID_ADDRESS: 'Недействительный адрес',
    MANUAL_BLOCK: 'Ручная блокировка',
    DELETED_IDENTITY: 'Удалённая идентичность',
  },
  validation: {
    completeEvidence:
      'Укажите продажу, email-идентичность, все пять условий, полные тексты и доказательства.',
  },
  messages: {
    submitted: 'Проверка подана в заблокированном состоянии.',
    reviewed: 'Решение записано.',
    settingsSaved: 'Настройка организации сохранена.',
    suppressed: 'Блокировка вступила в силу немедленно.',
    lifted: 'Ручная блокировка снята без автоматической реактивации.',
  },
} as const;

const audienceDe = {
  createTitle: 'Zielgruppe anlegen',
  editTitle: 'Neue Zielgruppenversion',
  name: 'Name',
  type: 'Typ',
  dynamic: 'Dynamisch',
  static: 'Statisch',
  description: 'Beschreibung',
  sources: 'Empfängerquellen',
  languages: 'Sprachen',
  countries: 'Länder (ISO-Codes)',
  regions: 'Regionen',
  customerGroups: 'Kundengruppen',
  productIds: 'Artikel-IDs',
  productGroups: 'Warengruppen',
  productCategoryIds: 'Kategorie-IDs',
  salesRepIds: 'Vertriebsmitarbeiter-IDs',
  tags: 'Tags',
  commaSeparated: 'Kommagetrennt',
  purchaseSince: 'Kauf seit',
  lastOrderFrom: 'Letzter Auftrag ab',
  lastOrderTo: 'Letzter Auftrag bis',
  revenueMin: 'Umsatz mindestens',
  revenueMax: 'Umsatz höchstens',
  topics: 'Aktive Newsletter-Themen',
  legalBases: 'Dokumentierte Rechtsgrundlagen',
  customField: 'Freigegebenes Zusatzfeld',
  noCustomField: 'Kein Zusatzfeld',
  customFieldValue: 'Zusatzfeldwert',
  staticRecipients: 'Statische Empfänger',
  deliveryTopic: 'Versandthema für Berechtigungsprüfung',
  preview: 'Vorschau prüfen',
  previewResult: 'Zielgruppenvorschau',
  total: 'Kandidaten',
  included: 'Eingeschlossen',
  excluded: 'Ausgeschlossen',
  previewNotice:
    'Die Vorschau dedupliziert nach normalisierter E-Mail und prüft Suppression sowie Berechtigung. Vor jedem Versand folgt erneut dieselbe serverseitige Prüfung.',
  savedSegments: 'Gespeicherte, versionierte Zielgruppen',
  source: {
    CUSTOMER: 'Kunden',
    PROSPECT: 'Interessenten',
    SUPPLIER_CONTACT: 'Lieferantenkontakte',
    PORTAL_USER: 'Portalbenutzer',
    PARTNERLESS: 'Frei angemeldete Empfänger',
  },
  validation: {
    segment: 'Zielgruppe und Filter bitte vollständig prüfen.',
    preview: 'Für die Vorschau sind eine gültige Definition und ein Versandthema erforderlich.',
  },
  messages: {
    saved: 'Zielgruppenversion gespeichert.',
    previewFailed: 'Zielgruppenvorschau fehlgeschlagen.',
  },
} as const;
const importDe = {
  title: 'CSV-/Excel-Empfängerimport',
  warning:
    'Gekaufte, ungeprüfte oder rechtswidrig beschaffte Listen dürfen nicht verwendet werden. Ein Import erteilt keine Einwilligung: neue Empfänger bleiben gesperrt, bis ein separater, dokumentierter Nachweis vorliegt.',
  file: 'CSV- oder XLSX-Datei',
  rows: 'Zeilen',
  skip: 'Nicht übernehmen',
  customerNumber: 'Kundennummer',
  defaultLocale: 'Standardsprache',
  validate: 'Validieren und Vorschau erzeugen',
  totalRows: 'Gesamt',
  createdRows: 'Neu gesperrt',
  matchedRows: 'Bestehend',
  suppressedRows: 'Unterdrückt',
  duplicateRows: 'Dubletten',
  invalidRows: 'Fehlerhaft',
  sourceDescription: 'Importquelle und Herkunftsnachweis',
  acknowledgement:
    'Ich bestätige, dass die Liste nicht gekauft oder rechtswidrig beschafft wurde und dass der Import keine Einwilligung erzeugt.',
  execute: 'Gesperrt importieren',
  downloadReport: 'Fehlerbericht laden',
  report: 'Validierungs- und Fehlerbericht',
  row: 'Zeile',
  reason: 'Grund',
  history: 'Importhistorie',
  validation: {
    execute: 'Quelle (mindestens 20 Zeichen) und Pflichtbestätigung sind erforderlich.',
  },
  messages: {
    uploadFailed: 'Datei konnte nicht gelesen werden.',
    dryRunFailed: 'Validierung fehlgeschlagen.',
    completed: 'Import wurde protokolliert; neue Empfänger bleiben gesperrt.',
    executeFailed: 'Import fehlgeschlagen.',
  },
} as const;
const audienceEn = {
  createTitle: 'Create audience',
  editTitle: 'New audience version',
  name: 'Name',
  type: 'Type',
  dynamic: 'Dynamic',
  static: 'Static',
  description: 'Description',
  sources: 'Recipient sources',
  languages: 'Languages',
  countries: 'Countries (ISO codes)',
  regions: 'Regions',
  customerGroups: 'Customer groups',
  productIds: 'Product IDs',
  productGroups: 'Product groups',
  productCategoryIds: 'Category IDs',
  salesRepIds: 'Sales representative IDs',
  tags: 'Tags',
  commaSeparated: 'Comma-separated',
  purchaseSince: 'Purchased since',
  lastOrderFrom: 'Last order from',
  lastOrderTo: 'Last order to',
  revenueMin: 'Minimum revenue',
  revenueMax: 'Maximum revenue',
  topics: 'Active newsletter topics',
  legalBases: 'Documented legal bases',
  customField: 'Approved custom field',
  noCustomField: 'No custom field',
  customFieldValue: 'Custom-field value',
  staticRecipients: 'Static recipients',
  deliveryTopic: 'Delivery topic for eligibility check',
  preview: 'Check preview',
  previewResult: 'Audience preview',
  total: 'Candidates',
  included: 'Included',
  excluded: 'Excluded',
  previewNotice:
    'The preview deduplicates normalized email addresses and checks suppression and eligibility. The same server-side check runs again before every send.',
  savedSegments: 'Saved, versioned audiences',
  source: {
    CUSTOMER: 'Customers',
    PROSPECT: 'Prospects',
    SUPPLIER_CONTACT: 'Supplier contacts',
    PORTAL_USER: 'Portal users',
    PARTNERLESS: 'Independent subscribers',
  },
  validation: {
    segment: 'Review the audience and filters.',
    preview: 'A valid definition and delivery topic are required.',
  },
  messages: { saved: 'Audience version saved.', previewFailed: 'Audience preview failed.' },
} as const;
const importEn = {
  title: 'CSV/Excel recipient import',
  warning:
    'Purchased, unchecked, or unlawfully obtained lists must not be used. Importing does not grant consent: new recipients remain blocked until separate evidence is documented.',
  file: 'CSV or XLSX file',
  rows: 'rows',
  skip: 'Skip',
  customerNumber: 'Customer number',
  defaultLocale: 'Default language',
  validate: 'Validate and preview',
  totalRows: 'Total',
  createdRows: 'Newly blocked',
  matchedRows: 'Existing',
  suppressedRows: 'Suppressed',
  duplicateRows: 'Duplicates',
  invalidRows: 'Invalid',
  sourceDescription: 'Import source and provenance evidence',
  acknowledgement:
    'I confirm that the list was not purchased or unlawfully obtained and that import does not create consent.',
  execute: 'Import as blocked',
  downloadReport: 'Download error report',
  report: 'Validation and error report',
  row: 'Row',
  reason: 'Reason',
  history: 'Import history',
  validation: {
    execute: 'A source description (at least 20 characters) and confirmation are required.',
  },
  messages: {
    uploadFailed: 'The file could not be read.',
    dryRunFailed: 'Validation failed.',
    completed: 'Import recorded; new recipients remain blocked.',
    executeFailed: 'Import failed.',
  },
} as const;
const audienceTr = {
  createTitle: 'Hedef kitle oluştur',
  editTitle: 'Yeni hedef kitle sürümü',
  name: 'Ad',
  type: 'Tür',
  dynamic: 'Dinamik',
  static: 'Statik',
  description: 'Açıklama',
  sources: 'Alıcı kaynakları',
  languages: 'Diller',
  countries: 'Ülkeler (ISO kodları)',
  regions: 'Bölgeler',
  customerGroups: 'Müşteri grupları',
  productIds: 'Ürün kimlikleri',
  productGroups: 'Ürün grupları',
  productCategoryIds: 'Kategori kimlikleri',
  salesRepIds: 'Satış temsilcisi kimlikleri',
  tags: 'Etiketler',
  commaSeparated: 'Virgülle ayrılmış',
  purchaseSince: 'Bu tarihten beri satın alma',
  lastOrderFrom: 'Son sipariş başlangıcı',
  lastOrderTo: 'Son sipariş bitişi',
  revenueMin: 'Asgari ciro',
  revenueMax: 'Azami ciro',
  topics: 'Etkin bülten konuları',
  legalBases: 'Belgelenmiş hukuki dayanaklar',
  customField: 'Onaylı özel alan',
  noCustomField: 'Özel alan yok',
  customFieldValue: 'Özel alan değeri',
  staticRecipients: 'Statik alıcılar',
  deliveryTopic: 'Uygunluk kontrolü konusu',
  preview: 'Önizlemeyi denetle',
  previewResult: 'Hedef kitle önizlemesi',
  total: 'Aday',
  included: 'Dahil',
  excluded: 'Hariç',
  previewNotice:
    'Önizleme normalleştirilmiş e-postaya göre tekilleştirir, engelleme ve uygunluğu denetler. Gönderimden önce aynı sunucu kontrolü yeniden yapılır.',
  savedSegments: 'Kaydedilmiş sürümlü hedef kitleler',
  source: {
    CUSTOMER: 'Müşteriler',
    PROSPECT: 'Potansiyel müşteriler',
    SUPPLIER_CONTACT: 'Tedarikçi kişileri',
    PORTAL_USER: 'Portal kullanıcıları',
    PARTNERLESS: 'Bağımsız aboneler',
  },
  validation: {
    segment: 'Hedef kitleyi ve filtreleri denetleyin.',
    preview: 'Geçerli tanım ve gönderim konusu gereklidir.',
  },
  messages: {
    saved: 'Hedef kitle sürümü kaydedildi.',
    previewFailed: 'Hedef kitle önizlemesi başarısız.',
  },
} as const;
const importTr = {
  title: 'CSV/Excel alıcı içe aktarma',
  warning:
    'Satın alınmış, denetlenmemiş veya hukuka aykırı elde edilmiş listeler kullanılamaz. İçe aktarma rıza vermez; yeni alıcılar ayrı kanıt belgelenene kadar engelli kalır.',
  file: 'CSV veya XLSX dosyası',
  rows: 'satır',
  skip: 'Atla',
  customerNumber: 'Müşteri numarası',
  defaultLocale: 'Varsayılan dil',
  validate: 'Doğrula ve önizle',
  totalRows: 'Toplam',
  createdRows: 'Yeni engelli',
  matchedRows: 'Mevcut',
  suppressedRows: 'Bastırılmış',
  duplicateRows: 'Yinelenen',
  invalidRows: 'Hatalı',
  sourceDescription: 'İçe aktarma kaynağı ve köken kanıtı',
  acknowledgement:
    'Listenin satın alınmadığını veya hukuka aykırı edinilmediğini ve içe aktarmanın rıza oluşturmadığını onaylıyorum.',
  execute: 'Engelli olarak içe aktar',
  downloadReport: 'Hata raporunu indir',
  report: 'Doğrulama ve hata raporu',
  row: 'Satır',
  reason: 'Neden',
  history: 'İçe aktarma geçmişi',
  validation: { execute: 'Kaynak açıklaması (en az 20 karakter) ve onay gereklidir.' },
  messages: {
    uploadFailed: 'Dosya okunamadı.',
    dryRunFailed: 'Doğrulama başarısız.',
    completed: 'İçe aktarma kaydedildi; yeni alıcılar engelli kaldı.',
    executeFailed: 'İçe aktarma başarısız.',
  },
} as const;
const audienceFr = {
  createTitle: 'Créer une audience',
  editTitle: 'Nouvelle version d’audience',
  name: 'Nom',
  type: 'Type',
  dynamic: 'Dynamique',
  static: 'Statique',
  description: 'Description',
  sources: 'Sources des destinataires',
  languages: 'Langues',
  countries: 'Pays (codes ISO)',
  regions: 'Régions',
  customerGroups: 'Groupes clients',
  productIds: 'Identifiants article',
  productGroups: 'Groupes de produits',
  productCategoryIds: 'Identifiants catégorie',
  salesRepIds: 'Identifiants commerciaux',
  tags: 'Étiquettes',
  commaSeparated: 'Séparé par des virgules',
  purchaseSince: 'Achat depuis',
  lastOrderFrom: 'Dernière commande à partir du',
  lastOrderTo: 'Dernière commande jusqu’au',
  revenueMin: 'CA minimum',
  revenueMax: 'CA maximum',
  topics: 'Thèmes actifs',
  legalBases: 'Bases juridiques documentées',
  customField: 'Champ personnalisé approuvé',
  noCustomField: 'Aucun champ',
  customFieldValue: 'Valeur du champ',
  staticRecipients: 'Destinataires statiques',
  deliveryTopic: 'Thème d’envoi pour le contrôle',
  preview: 'Vérifier l’aperçu',
  previewResult: 'Aperçu de l’audience',
  total: 'Candidats',
  included: 'Inclus',
  excluded: 'Exclus',
  previewNotice:
    'L’aperçu déduplique les e-mails normalisés et contrôle la suppression et l’éligibilité. Le même contrôle serveur est répété avant chaque envoi.',
  savedSegments: 'Audiences enregistrées et versionnées',
  source: {
    CUSTOMER: 'Clients',
    PROSPECT: 'Prospects',
    SUPPLIER_CONTACT: 'Contacts fournisseurs',
    PORTAL_USER: 'Utilisateurs du portail',
    PARTNERLESS: 'Abonnés indépendants',
  },
  validation: {
    segment: 'Vérifiez l’audience et les filtres.',
    preview: 'Une définition valide et un thème d’envoi sont requis.',
  },
  messages: { saved: 'Version d’audience enregistrée.', previewFailed: 'Échec de l’aperçu.' },
} as const;
const importFr = {
  title: 'Import CSV/Excel de destinataires',
  warning:
    'Les listes achetées, non vérifiées ou obtenues illicitement ne doivent pas être utilisées. L’import ne vaut pas consentement : les nouveaux destinataires restent bloqués jusqu’à preuve séparée.',
  file: 'Fichier CSV ou XLSX',
  rows: 'lignes',
  skip: 'Ignorer',
  customerNumber: 'Numéro client',
  defaultLocale: 'Langue par défaut',
  validate: 'Valider et prévisualiser',
  totalRows: 'Total',
  createdRows: 'Nouveaux bloqués',
  matchedRows: 'Existants',
  suppressedRows: 'Supprimés',
  duplicateRows: 'Doublons',
  invalidRows: 'Invalides',
  sourceDescription: 'Source et preuve de provenance',
  acknowledgement:
    'Je confirme que la liste n’a pas été achetée ni obtenue illicitement et que l’import ne crée aucun consentement.',
  execute: 'Importer comme bloqué',
  downloadReport: 'Télécharger le rapport d’erreurs',
  report: 'Rapport de validation et d’erreurs',
  row: 'Ligne',
  reason: 'Motif',
  history: 'Historique des imports',
  validation: { execute: 'Une source (20 caractères minimum) et la confirmation sont requises.' },
  messages: {
    uploadFailed: 'Lecture du fichier impossible.',
    dryRunFailed: 'Échec de la validation.',
    completed: 'Import consigné ; les nouveaux destinataires restent bloqués.',
    executeFailed: 'Échec de l’import.',
  },
} as const;
const audienceEs = {
  createTitle: 'Crear audiencia',
  editTitle: 'Nueva versión de audiencia',
  name: 'Nombre',
  type: 'Tipo',
  dynamic: 'Dinámica',
  static: 'Estática',
  description: 'Descripción',
  sources: 'Fuentes de destinatarios',
  languages: 'Idiomas',
  countries: 'Países (códigos ISO)',
  regions: 'Regiones',
  customerGroups: 'Grupos de clientes',
  productIds: 'ID de artículos',
  productGroups: 'Grupos de productos',
  productCategoryIds: 'ID de categorías',
  salesRepIds: 'ID de comerciales',
  tags: 'Etiquetas',
  commaSeparated: 'Separado por comas',
  purchaseSince: 'Compra desde',
  lastOrderFrom: 'Último pedido desde',
  lastOrderTo: 'Último pedido hasta',
  revenueMin: 'Ingresos mínimos',
  revenueMax: 'Ingresos máximos',
  topics: 'Temas activos',
  legalBases: 'Bases jurídicas documentadas',
  customField: 'Campo personalizado aprobado',
  noCustomField: 'Sin campo',
  customFieldValue: 'Valor del campo',
  staticRecipients: 'Destinatarios estáticos',
  deliveryTopic: 'Tema de envío para comprobar elegibilidad',
  preview: 'Comprobar vista previa',
  previewResult: 'Vista previa de audiencia',
  total: 'Candidatos',
  included: 'Incluidos',
  excluded: 'Excluidos',
  previewNotice:
    'La vista previa deduplica correos normalizados y comprueba supresión y elegibilidad. El mismo control del servidor se repite antes de cada envío.',
  savedSegments: 'Audiencias guardadas y versionadas',
  source: {
    CUSTOMER: 'Clientes',
    PROSPECT: 'Interesados',
    SUPPLIER_CONTACT: 'Contactos de proveedores',
    PORTAL_USER: 'Usuarios del portal',
    PARTNERLESS: 'Suscriptores independientes',
  },
  validation: {
    segment: 'Revise la audiencia y los filtros.',
    preview: 'Se requieren una definición válida y un tema de envío.',
  },
  messages: { saved: 'Versión de audiencia guardada.', previewFailed: 'Falló la vista previa.' },
} as const;
const importEs = {
  title: 'Importación CSV/Excel de destinatarios',
  warning:
    'No deben usarse listas compradas, no verificadas u obtenidas ilícitamente. Importar no concede consentimiento: los nuevos destinatarios quedan bloqueados hasta documentar una prueba separada.',
  file: 'Archivo CSV o XLSX',
  rows: 'filas',
  skip: 'Omitir',
  customerNumber: 'Número de cliente',
  defaultLocale: 'Idioma predeterminado',
  validate: 'Validar y previsualizar',
  totalRows: 'Total',
  createdRows: 'Nuevos bloqueados',
  matchedRows: 'Existentes',
  suppressedRows: 'Suprimidos',
  duplicateRows: 'Duplicados',
  invalidRows: 'No válidos',
  sourceDescription: 'Fuente y prueba de procedencia',
  acknowledgement:
    'Confirmo que la lista no fue comprada ni obtenida ilícitamente y que importar no crea consentimiento.',
  execute: 'Importar como bloqueados',
  downloadReport: 'Descargar informe de errores',
  report: 'Informe de validación y errores',
  row: 'Fila',
  reason: 'Motivo',
  history: 'Historial de importación',
  validation: { execute: 'Se requieren una fuente (mínimo 20 caracteres) y la confirmación.' },
  messages: {
    uploadFailed: 'No se pudo leer el archivo.',
    dryRunFailed: 'Falló la validación.',
    completed: 'Importación registrada; los nuevos destinatarios siguen bloqueados.',
    executeFailed: 'Falló la importación.',
  },
} as const;
const audienceRu = {
  createTitle: 'Создать аудиторию',
  editTitle: 'Новая версия аудитории',
  name: 'Название',
  type: 'Тип',
  dynamic: 'Динамическая',
  static: 'Статическая',
  description: 'Описание',
  sources: 'Источники получателей',
  languages: 'Языки',
  countries: 'Страны (коды ISO)',
  regions: 'Регионы',
  customerGroups: 'Группы клиентов',
  productIds: 'ID товаров',
  productGroups: 'Группы товаров',
  productCategoryIds: 'ID категорий',
  salesRepIds: 'ID менеджеров',
  tags: 'Теги',
  commaSeparated: 'Через запятую',
  purchaseSince: 'Покупки с даты',
  lastOrderFrom: 'Последний заказ с',
  lastOrderTo: 'Последний заказ по',
  revenueMin: 'Минимальная выручка',
  revenueMax: 'Максимальная выручка',
  topics: 'Активные темы',
  legalBases: 'Документированные правовые основания',
  customField: 'Разрешённое пользовательское поле',
  noCustomField: 'Без поля',
  customFieldValue: 'Значение поля',
  staticRecipients: 'Статические получатели',
  deliveryTopic: 'Тема отправки для проверки',
  preview: 'Проверить выборку',
  previewResult: 'Предпросмотр аудитории',
  total: 'Кандидаты',
  included: 'Включены',
  excluded: 'Исключены',
  previewNotice:
    'Предпросмотр устраняет дубли нормализованных адресов и проверяет блокировки и допустимость. Та же серверная проверка повторяется перед каждой отправкой.',
  savedSegments: 'Сохранённые версии аудиторий',
  source: {
    CUSTOMER: 'Клиенты',
    PROSPECT: 'Потенциальные клиенты',
    SUPPLIER_CONTACT: 'Контакты поставщиков',
    PORTAL_USER: 'Пользователи портала',
    PARTNERLESS: 'Независимые подписчики',
  },
  validation: {
    segment: 'Проверьте аудиторию и фильтры.',
    preview: 'Нужны корректное определение и тема отправки.',
  },
  messages: { saved: 'Версия аудитории сохранена.', previewFailed: 'Ошибка предпросмотра.' },
} as const;
const importRu = {
  title: 'Импорт получателей CSV/Excel',
  warning:
    'Нельзя использовать купленные, непроверенные или незаконно полученные списки. Импорт не означает согласие: новые получатели остаются заблокированными до отдельного документирования основания.',
  file: 'Файл CSV или XLSX',
  rows: 'строк',
  skip: 'Пропустить',
  customerNumber: 'Номер клиента',
  defaultLocale: 'Язык по умолчанию',
  validate: 'Проверить и показать',
  totalRows: 'Всего',
  createdRows: 'Новые заблокированные',
  matchedRows: 'Существующие',
  suppressedRows: 'Подавленные',
  duplicateRows: 'Дубликаты',
  invalidRows: 'Ошибочные',
  sourceDescription: 'Источник и подтверждение происхождения',
  acknowledgement:
    'Подтверждаю, что список не был куплен или незаконно получен и что импорт не создаёт согласия.',
  execute: 'Импортировать заблокированными',
  downloadReport: 'Скачать отчёт об ошибках',
  report: 'Отчёт проверки и ошибок',
  row: 'Строка',
  reason: 'Причина',
  history: 'История импорта',
  validation: { execute: 'Требуются описание источника (минимум 20 символов) и подтверждение.' },
  messages: {
    uploadFailed: 'Не удалось прочитать файл.',
    dryRunFailed: 'Ошибка проверки.',
    completed: 'Импорт зарегистрирован; новые получатели заблокированы.',
    executeFailed: 'Ошибка импорта.',
  },
} as const;

export const newsletterDe = {
  ...newsletterAdministrationDe,
  public: newsletterPublicDe,
  compliance: complianceDe,
  editor: newsletterEditorDe,
  deliverability: newsletterDeliverabilityDe,
  analytics: newsletterAnalyticsDe,
  title: 'Newsletter & E-Mail-Marketing',
  subtitle:
    'Mandantengetrennte Stammdaten und nachvollziehbare Einwilligungen für Marketingkommunikation.',
  legalNotice:
    'Das Modul unterstützt technische Prüfungen und Nachweise, ersetzt aber keine rechtliche oder datenschutzrechtliche Prüfung vor dem Produktivbetrieb.',
  tabs: {
    overview: 'Übersicht',
    campaigns: 'Kampagnen',
    templates: 'Vorlagen',
    audiences: 'Zielgruppen',
    imports: 'Import',
    recipients: 'Empfänger',
    consents: 'Einwilligungen',
    settings: 'Einstellungen',
  },
  audience: audienceDe,
  import: importDe,
  actions: {
    newBrand: 'Neue Marke',
    newTopic: 'Neues Thema',
    documentConsent: 'Einwilligung dokumentieren',
    history: 'Historie',
  },
  cards: { brands: 'Aktive Marken', topics: 'Aktive Themen', recipients: 'Empfänger' },
  sections: { topics: 'Newsletter-Themen', immutableHistory: 'Unveränderliche Historie' },
  visibility: { public: 'Öffentlich', internal: 'Intern' },
  filters: { search: 'E-Mail suchen', status: 'Status filtern', allStatuses: 'Alle Status' },
  table: {
    email: 'E-Mail',
    status: 'Status',
    language: 'Sprache',
    legalBasis: 'Rechtsgrundlage',
    topics: 'Themen',
    source: 'Quelle',
    actions: 'Aktionen',
  },
  fields: {
    key: 'Technischer Schlüssel',
    name: 'Name',
    description: 'Beschreibung',
    senderCompany: 'Absendendes Unternehmen',
    brand: 'Marke',
    selectBrand: 'Marke auswählen',
    purpose: 'Einwilligungszweck',
    publicTopic: 'Thema im öffentlichen Formular anbieten',
    email: 'E-Mail-Adresse',
    language: 'Bevorzugte Sprache',
    documentedAt: 'Nachweisdatum',
    dmsEvidence: 'DMS-Nachweis-ID (optional)',
    topics: 'Frei gewählte Themen',
    reason: 'Begründung',
    evidence: 'Vollständige Nachweisbeschreibung',
    consentVersion: 'Version des Einwilligungstexts',
    privacyVersion: 'Version des Datenschutzhinweises',
    consentText: 'Einwilligungstext',
    privacyText: 'Datenschutzhinweis',
    protectedTechnicalEvidence: 'Geschützte technische Nachweisdaten',
  },
  dialogs: {
    brandTitle: 'Newsletter-Marke anlegen',
    brandDescription: 'Die Marke bezeichnet das absendende Unternehmen.',
    topicTitle: 'Mehrsprachiges Newsletter-Thema',
    topicDescription: 'Name und Beschreibung sind in allen ERP-Sprachen erforderlich.',
    consentTitle: 'Manuelle Einwilligung dokumentieren',
    consentDescription:
      'Nur einen bereits vollständig belegten Vorgang erfassen. Diese Funktion erzeugt keine Einwilligung durch einen Schalter.',
  },
  validation: {
    completeFields: 'Bitte alle Pflichtfelder korrekt ausfüllen.',
    allTranslations: 'Bitte alle Übersetzungen und Pflichtfelder vollständig ausfüllen.',
    completeEvidence:
      'Bitte Themen, Texte, Versionen, Begründung und Nachweis vollständig erfassen.',
  },
  messages: {
    brandCreated: 'Marke wurde angelegt.',
    topicCreated: 'Thema wurde angelegt.',
    consentDocumented: 'Einwilligungsnachweis wurde unveränderlich dokumentiert.',
    saveFailed: 'Speichern fehlgeschlagen.',
    loadFailed: 'Die Einwilligungshistorie konnte nicht geladen werden.',
  },
  empty: {
    topics: 'Noch keine Newsletter-Themen vorhanden.',
    recipients: 'Keine Empfänger gefunden.',
    selectRecipient: 'Wählen Sie in der Empfängerliste eine Person aus, um die Historie zu sehen.',
  },
  status: statusDe,
  legalBasis: legalBasis.de,
  source: source.de,
  event: event.de,
} as const;

export const newsletterEn = {
  ...newsletterDe,
  ...newsletterAdministrationEn,
  audience: audienceEn,
  import: importEn,
  public: newsletterPublicEn,
  editor: newsletterEditorEn,
  deliverability: newsletterDeliverabilityEn,
  analytics: newsletterAnalyticsEn,
  title: 'Newsletters & email marketing',
  subtitle:
    'Tenant-isolated master data and traceable consent records for marketing communication.',
  legalNotice:
    'This module supports technical checks and evidence, but does not replace legal or data-protection review before production use.',
  tabs: {
    overview: 'Overview',
    campaigns: 'Campaigns',
    templates: 'Templates',
    audiences: 'Audiences',
    imports: 'Import',
    recipients: 'Recipients',
    consents: 'Consents',
    settings: 'Settings',
  },
  actions: {
    newBrand: 'New brand',
    newTopic: 'New topic',
    documentConsent: 'Document consent',
    history: 'History',
  },
  cards: { brands: 'Active brands', topics: 'Active topics', recipients: 'Recipients' },
  sections: { topics: 'Newsletter topics', immutableHistory: 'Immutable history' },
  visibility: { public: 'Public', internal: 'Internal' },
  filters: { search: 'Search email', status: 'Filter status', allStatuses: 'All statuses' },
  table: {
    email: 'Email',
    status: 'Status',
    language: 'Language',
    legalBasis: 'Legal basis',
    topics: 'Topics',
    source: 'Source',
    actions: 'Actions',
  },
  fields: {
    key: 'Technical key',
    name: 'Name',
    description: 'Description',
    senderCompany: 'Sending company',
    brand: 'Brand',
    selectBrand: 'Select brand',
    purpose: 'Consent purpose',
    publicTopic: 'Offer topic on the public form',
    email: 'Email address',
    language: 'Preferred language',
    documentedAt: 'Evidence date',
    dmsEvidence: 'DMS evidence ID (optional)',
    topics: 'Freely selected topics',
    reason: 'Reason',
    evidence: 'Complete evidence description',
    consentVersion: 'Consent text version',
    privacyVersion: 'Privacy notice version',
    consentText: 'Consent text',
    privacyText: 'Privacy notice',
    protectedTechnicalEvidence: 'Protected technical evidence',
  },
  dialogs: {
    brandTitle: 'Create newsletter brand',
    brandDescription: 'The brand identifies the sending company.',
    topicTitle: 'Multilingual newsletter topic',
    topicDescription: 'Name and description are required in every ERP language.',
    consentTitle: 'Document manual consent',
    consentDescription:
      'Record only a fully evidenced event. This function cannot create consent with a toggle.',
  },
  validation: {
    completeFields: 'Complete all required fields correctly.',
    allTranslations: 'Complete every translation and required field.',
    completeEvidence: 'Complete topics, texts, versions, reason and evidence.',
  },
  messages: {
    brandCreated: 'Brand created.',
    topicCreated: 'Topic created.',
    consentDocumented: 'Consent evidence was recorded immutably.',
    saveFailed: 'Could not save.',
    loadFailed: 'Consent history could not be loaded.',
  },
  empty: {
    topics: 'No newsletter topics yet.',
    recipients: 'No recipients found.',
    selectRecipient: 'Select a person from the recipient list to view the history.',
  },
  status: statusEn,
  legalBasis: legalBasis.en,
  source: source.en,
  event: event.en,
  compliance: complianceEn,
} as const;

export const newsletterTr = {
  ...newsletterEn,
  ...newsletterAdministrationTr,
  audience: audienceTr,
  import: importTr,
  public: newsletterPublicTr,
  editor: newsletterEditorTr,
  deliverability: newsletterDeliverabilityTr,
  analytics: newsletterAnalyticsTr,
  title: 'Bülten ve e-posta pazarlaması',
  subtitle:
    'Pazarlama iletişimi için kiracı bazında ayrılmış ana veriler ve izlenebilir rıza kayıtları.',
  legalNotice:
    'Bu modül teknik kontrolleri ve kanıtları destekler; üretim öncesi hukuk ve veri koruma incelemesinin yerini almaz.',
  tabs: {
    overview: 'Genel bakış',
    campaigns: 'Kampanyalar',
    templates: 'Şablonlar',
    audiences: 'Hedef kitleler',
    imports: 'İçe aktarma',
    recipients: 'Alıcılar',
    consents: 'Rızalar',
    settings: 'Ayarlar',
  },
  actions: {
    newBrand: 'Yeni marka',
    newTopic: 'Yeni konu',
    documentConsent: 'Rızayı belgele',
    history: 'Geçmiş',
  },
  cards: { brands: 'Aktif markalar', topics: 'Aktif konular', recipients: 'Alıcılar' },
  sections: { topics: 'Bülten konuları', immutableHistory: 'Değiştirilemez geçmiş' },
  visibility: { public: 'Herkese açık', internal: 'Dahili' },
  filters: { search: 'E-posta ara', status: 'Durumu filtrele', allStatuses: 'Tüm durumlar' },
  table: {
    email: 'E-posta',
    status: 'Durum',
    language: 'Dil',
    legalBasis: 'Hukuki dayanak',
    topics: 'Konular',
    source: 'Kaynak',
    actions: 'İşlemler',
  },
  fields: {
    key: 'Teknik anahtar',
    name: 'Ad',
    description: 'Açıklama',
    senderCompany: 'Gönderen şirket',
    brand: 'Marka',
    selectBrand: 'Marka seçin',
    purpose: 'Rıza amacı',
    publicTopic: 'Konuyu herkese açık formda sun',
    email: 'E-posta adresi',
    language: 'Tercih edilen dil',
    documentedAt: 'Kanıt tarihi',
    dmsEvidence: 'DMS kanıt kimliği (isteğe bağlı)',
    topics: 'Özgürce seçilen konular',
    reason: 'Gerekçe',
    evidence: 'Eksiksiz kanıt açıklaması',
    consentVersion: 'Rıza metni sürümü',
    privacyVersion: 'Gizlilik bildirimi sürümü',
    consentText: 'Rıza metni',
    privacyText: 'Gizlilik bildirimi',
    protectedTechnicalEvidence: 'Korunan teknik kanıt',
  },
  dialogs: {
    brandTitle: 'Bülten markası oluştur',
    brandDescription: 'Marka, gönderici şirketi tanımlar.',
    topicTitle: 'Çok dilli bülten konusu',
    topicDescription: 'Ad ve açıklama tüm ERP dillerinde gereklidir.',
    consentTitle: 'Elle rıza belgele',
    consentDescription:
      'Yalnızca tam kanıtlı bir olayı kaydedin. Bu işlev bir anahtarla rıza oluşturmaz.',
  },
  validation: {
    completeFields: 'Tüm zorunlu alanları doğru doldurun.',
    allTranslations: 'Tüm çevirileri ve zorunlu alanları doldurun.',
    completeEvidence: 'Konuları, metinleri, sürümleri, nedeni ve kanıtı tamamlayın.',
  },
  messages: {
    brandCreated: 'Marka oluşturuldu.',
    topicCreated: 'Konu oluşturuldu.',
    consentDocumented: 'Rıza kanıtı değiştirilemez biçimde kaydedildi.',
    saveFailed: 'Kaydedilemedi.',
    loadFailed: 'Rıza geçmişi yüklenemedi.',
  },
  empty: {
    topics: 'Henüz bülten konusu yok.',
    recipients: 'Alıcı bulunamadı.',
    selectRecipient: 'Geçmişi görmek için alıcı listesinden bir kişi seçin.',
  },
  status: statusTr,
  legalBasis: legalBasis.tr,
  source: source.tr,
  event: event.tr,
  compliance: complianceTr,
} as const;

export const newsletterFr = {
  ...newsletterEn,
  ...newsletterAdministrationFr,
  audience: audienceFr,
  import: importFr,
  public: newsletterPublicFr,
  editor: newsletterEditorFr,
  deliverability: newsletterDeliverabilityFr,
  analytics: newsletterAnalyticsFr,
  title: 'Newsletters et marketing par e-mail',
  subtitle:
    'Données de référence isolées par locataire et consentements traçables pour les communications marketing.',
  legalNotice:
    'Ce module facilite les contrôles techniques et les preuves, mais ne remplace pas l’examen juridique et de protection des données avant la production.',
  tabs: {
    overview: 'Vue d’ensemble',
    campaigns: 'Campagnes',
    templates: 'Modèles',
    audiences: 'Audiences',
    imports: 'Import',
    recipients: 'Destinataires',
    consents: 'Consentements',
    settings: 'Paramètres',
  },
  actions: {
    newBrand: 'Nouvelle marque',
    newTopic: 'Nouveau thème',
    documentConsent: 'Documenter le consentement',
    history: 'Historique',
  },
  cards: { brands: 'Marques actives', topics: 'Thèmes actifs', recipients: 'Destinataires' },
  sections: { topics: 'Thèmes de newsletter', immutableHistory: 'Historique immuable' },
  visibility: { public: 'Public', internal: 'Interne' },
  filters: {
    search: 'Rechercher un e-mail',
    status: 'Filtrer le statut',
    allStatuses: 'Tous les statuts',
  },
  table: {
    email: 'E-mail',
    status: 'Statut',
    language: 'Langue',
    legalBasis: 'Base juridique',
    topics: 'Thèmes',
    source: 'Source',
    actions: 'Actions',
  },
  fields: {
    key: 'Clé technique',
    name: 'Nom',
    description: 'Description',
    senderCompany: 'Entreprise expéditrice',
    brand: 'Marque',
    selectBrand: 'Sélectionner une marque',
    purpose: 'Finalité du consentement',
    publicTopic: 'Proposer le thème dans le formulaire public',
    email: 'Adresse e-mail',
    language: 'Langue préférée',
    documentedAt: 'Date de la preuve',
    dmsEvidence: 'Identifiant de preuve DMS (facultatif)',
    topics: 'Thèmes librement choisis',
    reason: 'Motif',
    evidence: 'Description complète de la preuve',
    consentVersion: 'Version du texte de consentement',
    privacyVersion: 'Version de l’avis de confidentialité',
    consentText: 'Texte de consentement',
    privacyText: 'Avis de confidentialité',
    protectedTechnicalEvidence: 'Preuve technique protégée',
  },
  dialogs: {
    brandTitle: 'Créer une marque',
    brandDescription: 'La marque identifie l’entreprise expéditrice.',
    topicTitle: 'Thème multilingue',
    topicDescription: 'Le nom et la description sont requis dans chaque langue ERP.',
    consentTitle: 'Documenter un consentement manuel',
    consentDescription:
      'Enregistrez uniquement un événement entièrement prouvé. Cette fonction ne crée pas un consentement par interrupteur.',
  },
  validation: {
    completeFields: 'Renseignez correctement tous les champs obligatoires.',
    allTranslations: 'Complétez toutes les traductions et tous les champs obligatoires.',
    completeEvidence: 'Complétez thèmes, textes, versions, motif et preuve.',
  },
  messages: {
    brandCreated: 'Marque créée.',
    topicCreated: 'Thème créé.',
    consentDocumented: 'La preuve du consentement a été enregistrée de façon immuable.',
    saveFailed: 'Enregistrement impossible.',
    loadFailed: 'Impossible de charger l’historique.',
  },
  empty: {
    topics: 'Aucun thème de newsletter.',
    recipients: 'Aucun destinataire trouvé.',
    selectRecipient: 'Sélectionnez une personne dans la liste pour consulter son historique.',
  },
  status: statusFr,
  legalBasis: legalBasis.fr,
  source: source.fr,
  event: event.fr,
  compliance: complianceFr,
} as const;

export const newsletterEs = {
  ...newsletterEn,
  ...newsletterAdministrationEs,
  audience: audienceEs,
  import: importEs,
  public: newsletterPublicEs,
  editor: newsletterEditorEs,
  deliverability: newsletterDeliverabilityEs,
  analytics: newsletterAnalyticsEs,
  title: 'Boletines y marketing por correo',
  subtitle:
    'Datos maestros aislados por tenant y consentimientos trazables para comunicaciones comerciales.',
  legalNotice:
    'Este módulo facilita controles técnicos y pruebas, pero no sustituye la revisión jurídica y de protección de datos antes de producción.',
  tabs: {
    overview: 'Resumen',
    campaigns: 'Campañas',
    templates: 'Plantillas',
    audiences: 'Audiencias',
    imports: 'Importación',
    recipients: 'Destinatarios',
    consents: 'Consentimientos',
    settings: 'Ajustes',
  },
  actions: {
    newBrand: 'Nueva marca',
    newTopic: 'Nuevo tema',
    documentConsent: 'Documentar consentimiento',
    history: 'Historial',
  },
  cards: { brands: 'Marcas activas', topics: 'Temas activos', recipients: 'Destinatarios' },
  sections: { topics: 'Temas del boletín', immutableHistory: 'Historial inmutable' },
  visibility: { public: 'Público', internal: 'Interno' },
  filters: { search: 'Buscar correo', status: 'Filtrar estado', allStatuses: 'Todos los estados' },
  table: {
    email: 'Correo',
    status: 'Estado',
    language: 'Idioma',
    legalBasis: 'Base jurídica',
    topics: 'Temas',
    source: 'Fuente',
    actions: 'Acciones',
  },
  fields: {
    key: 'Clave técnica',
    name: 'Nombre',
    description: 'Descripción',
    senderCompany: 'Empresa remitente',
    brand: 'Marca',
    selectBrand: 'Seleccionar marca',
    purpose: 'Finalidad del consentimiento',
    publicTopic: 'Ofrecer el tema en el formulario público',
    email: 'Dirección de correo',
    language: 'Idioma preferido',
    documentedAt: 'Fecha de la prueba',
    dmsEvidence: 'ID de prueba DMS (opcional)',
    topics: 'Temas elegidos libremente',
    reason: 'Motivo',
    evidence: 'Descripción completa de la prueba',
    consentVersion: 'Versión del texto de consentimiento',
    privacyVersion: 'Versión del aviso de privacidad',
    consentText: 'Texto de consentimiento',
    privacyText: 'Aviso de privacidad',
    protectedTechnicalEvidence: 'Prueba técnica protegida',
  },
  dialogs: {
    brandTitle: 'Crear marca de boletín',
    brandDescription: 'La marca identifica a la empresa remitente.',
    topicTitle: 'Tema multilingüe',
    topicDescription: 'El nombre y la descripción son obligatorios en cada idioma del ERP.',
    consentTitle: 'Documentar consentimiento manual',
    consentDescription:
      'Registre solo un hecho con prueba completa. Esta función no crea consentimiento mediante un interruptor.',
  },
  validation: {
    completeFields: 'Complete correctamente todos los campos obligatorios.',
    allTranslations: 'Complete todas las traducciones y campos obligatorios.',
    completeEvidence: 'Complete temas, textos, versiones, motivo y prueba.',
  },
  messages: {
    brandCreated: 'Marca creada.',
    topicCreated: 'Tema creado.',
    consentDocumented: 'La prueba del consentimiento se registró de forma inmutable.',
    saveFailed: 'No se pudo guardar.',
    loadFailed: 'No se pudo cargar el historial.',
  },
  empty: {
    topics: 'Todavía no hay temas.',
    recipients: 'No se encontraron destinatarios.',
    selectRecipient: 'Seleccione una persona de la lista para ver el historial.',
  },
  status: statusEs,
  legalBasis: legalBasis.es,
  source: source.es,
  event: event.es,
  compliance: complianceEs,
} as const;

export const newsletterRu = {
  ...newsletterEn,
  ...newsletterAdministrationRu,
  audience: audienceRu,
  import: importRu,
  public: newsletterPublicRu,
  editor: newsletterEditorRu,
  deliverability: newsletterDeliverabilityRu,
  analytics: newsletterAnalyticsRu,
  title: 'Рассылки и email-маркетинг',
  subtitle:
    'Изолированные по организациям справочники и прослеживаемые согласия для маркетинговых сообщений.',
  legalNotice:
    'Модуль помогает с техническими проверками и подтверждениями, но не заменяет юридическую проверку и проверку защиты данных перед эксплуатацией.',
  tabs: {
    overview: 'Обзор',
    campaigns: 'Кампании',
    templates: 'Шаблоны',
    audiences: 'Аудитории',
    imports: 'Импорт',
    recipients: 'Получатели',
    consents: 'Согласия',
    settings: 'Настройки',
  },
  actions: {
    newBrand: 'Новый бренд',
    newTopic: 'Новая тема',
    documentConsent: 'Задокументировать согласие',
    history: 'История',
  },
  cards: { brands: 'Активные бренды', topics: 'Активные темы', recipients: 'Получатели' },
  sections: { topics: 'Темы рассылки', immutableHistory: 'Неизменяемая история' },
  visibility: { public: 'Публичная', internal: 'Внутренняя' },
  filters: { search: 'Поиск email', status: 'Фильтр статуса', allStatuses: 'Все статусы' },
  table: {
    email: 'Email',
    status: 'Статус',
    language: 'Язык',
    legalBasis: 'Правовое основание',
    topics: 'Темы',
    source: 'Источник',
    actions: 'Действия',
  },
  fields: {
    key: 'Технический ключ',
    name: 'Название',
    description: 'Описание',
    senderCompany: 'Компания-отправитель',
    brand: 'Бренд',
    selectBrand: 'Выберите бренд',
    purpose: 'Цель согласия',
    publicTopic: 'Показывать тему в публичной форме',
    email: 'Адрес электронной почты',
    language: 'Предпочитаемый язык',
    documentedAt: 'Дата подтверждения',
    dmsEvidence: 'ID подтверждения DMS (необязательно)',
    topics: 'Свободно выбранные темы',
    reason: 'Причина',
    evidence: 'Полное описание подтверждения',
    consentVersion: 'Версия текста согласия',
    privacyVersion: 'Версия уведомления о конфиденциальности',
    consentText: 'Текст согласия',
    privacyText: 'Уведомление о конфиденциальности',
    protectedTechnicalEvidence: 'Защищённые технические данные',
  },
  dialogs: {
    brandTitle: 'Создать бренд рассылки',
    brandDescription: 'Бренд обозначает компанию-отправителя.',
    topicTitle: 'Многоязычная тема',
    topicDescription: 'Название и описание обязательны на всех языках ERP.',
    consentTitle: 'Подтвердить согласие вручную',
    consentDescription:
      'Регистрируйте только полностью подтверждённое событие. Эта функция не создаёт согласие переключателем.',
  },
  validation: {
    completeFields: 'Правильно заполните все обязательные поля.',
    allTranslations: 'Заполните все переводы и обязательные поля.',
    completeEvidence: 'Заполните темы, тексты, версии, причину и подтверждение.',
  },
  messages: {
    brandCreated: 'Бренд создан.',
    topicCreated: 'Тема создана.',
    consentDocumented: 'Подтверждение согласия записано неизменяемо.',
    saveFailed: 'Не удалось сохранить.',
    loadFailed: 'Не удалось загрузить историю.',
  },
  empty: {
    topics: 'Тем рассылки пока нет.',
    recipients: 'Получатели не найдены.',
    selectRecipient: 'Выберите получателя в списке, чтобы увидеть историю.',
  },
  status: statusRu,
  legalBasis: legalBasis.ru,
  source: source.ru,
  event: event.ru,
  compliance: complianceRu,
} as const;
